import { Fragment } from 'react';

/**
 * Line-break hints for a long identifier (e-mail, username, mailbox address,
 * snake_case reason code) in a narrow cell. An e-mail may wrap right after its
 * "@", and inside the local part before a "." or after a "_"; the domain is
 * kept whole (a hyphen is already a break point), so
 * "golibal.flooder.x@protonmail.com" wraps as "golibal.flooder.x@ /
 * protonmail.com" instead of mid-word or before ".com". Any other value may
 * wrap before a "." or after a "_". Presentation only: the text (and what a
 * copy yields) is unchanged, a value that fits its line does not wrap, and the
 * cell's own overflow-wrap still breaks a single part wider than the column.
 */
export function SoftBreaks({ text }: { readonly text: string }): JSX.Element {
  const at = text.lastIndexOf('@');
  const parts =
    at > 0
      ? [...text.slice(0, at + 1).split(/(?<=[@_])|(?=\.)/), text.slice(at + 1)]
      : text.split(/(?<=_)|(?=\.)/);
  return (
    <>
      {parts
        .filter((p) => p !== '')
        .map((part, i) => (
          <Fragment key={i}>
            {i > 0 ? <wbr /> : null}
            {part}
          </Fragment>
        ))}
    </>
  );
}

/**
 * Break hints for one machine token: a server code, a key=value pair, a path
 * or URL ("imap_authentication_failed_invalid_credentials",
 * "parent=login_failed/unknown_session_refresh_pending",
 * "https://api.kopechka.com/integrations/support-chat/v1"). It may wrap after
 * "_", "=", "&", "?", "@" and a "/" (not between the two of "//"), and before
 * a "." that starts a new part (".net"; not a sentence's final "." nor the
 * "." of a number / timestamp, "0.0400", "22:10:00.000Z"), so a narrow card
 * wraps it at a part boundary instead of at an arbitrary letter. An e-mail
 * keeps its domain whole (its hyphens still break), as in SoftBreaks.
 * The parts stay sibling text nodes of the caller's element; the text (and
 * what a copy yields) is unchanged, and the caller's overflow-wrap still
 * breaks a single part wider than the line. Use it where the token starts a
 * line of its own (a cell / block of its own); inside running text use
 * TextTokenBreaks, or a token that would fit the next line may be split at
 * the end of the current one.
 */
export function TokenBreaks({ text }: { readonly text: string }): JSX.Element {
  const parts = splitToken(text);
  if (parts.length < 2) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          {part}
        </Fragment>
      ))}
    </>
  );
}

const TOKEN_BREAK = /(?<=[_=&?@]|\/(?!\/))|(?<!\d)(?=\.[^\s.])/;

function splitToken(text: string): string[] {
  // An e-mail keeps its domain whole, as in SoftBreaks ("…@mail" / ".com"
  // would orphan the TLD); only its local part gets the hints.
  const at = text.lastIndexOf('@');
  const domain = at > 0 ? text.slice(at + 1) : '';
  const parts =
    at > 0 && /^[\w.-]+\.[A-Za-z]{2,}$/.test(domain)
      ? [...text.slice(0, at + 1).split(TOKEN_BREAK), domain]
      : text.split(TOKEN_BREAK);
  return parts.filter((p) => p !== '');
}

/**
 * Running text (a reason sentence, "key=value key=value" evidence) whose
 * machine tokens need the TokenBreaks hints. A token that can fit on a line
 * of its own (up to 60 characters) is an inline-block (.tok-brk): like
 * any word it moves to the next line whole instead of being split at the end
 * of the current one, and it wraps inside, at its part boundaries, only on a
 * line narrower than itself. A longer token (a URL, a chained reason code) is
 * wider than a card or table-cell line anyway: it flows inline with the
 * hints, so it continues the current line and wraps at a part boundary
 * instead of at an arbitrary letter. Plain words stay plain text, and text
 * without such a token is returned unchanged (one text node). Presentation
 * only: the text content is unchanged.
 */
export function TextTokenBreaks({ text }: { readonly text: string }): JSX.Element {
  const words = text.split(/(\s+)/);
  if (!words.some(isLongToken)) return <>{text}</>;
  return (
    <>
      {words.map((w, i) =>
        !isLongToken(w) ? (
          <Fragment key={i}>{w}</Fragment>
        ) : w.length <= MAX_WHOLE_TOKEN ? (
          <span key={i} className="tok-brk">
            <TokenBreaks text={w} />
          </span>
        ) : (
          <TokenBreaks key={i} text={w} />
        ),
      )}
    </>
  );
}

/** Longest token TextTokenBreaks still keeps whole when it fits a line. */
const MAX_WHOLE_TOKEN = 60;

function isLongToken(word: string): boolean {
  return word.length >= 16 && !/\s/.test(word) && splitToken(word).length > 1;
}

/**
 * Alert / rule names ("ProviderSellableZero_mailcom_engineer_com",
 * "PaymentWebhookSignatureRejected"): like SoftBreaks, plus — for a single
 * token without spaces — a break hint where a lower-case letter meets an
 * upper-case one, so a CamelCase name wraps as "PaymentWebhookSignature" /
 * "Rejected" instead of at an arbitrary letter. Titles with spaces get the
 * plain SoftBreaks hints. Presentation only: the text is unchanged.
 */
export function RuleNameBreaks({ text }: { readonly text: string }): JSX.Element {
  if (/\s/.test(text)) return <SoftBreaks text={text} />;
  const parts = text.split(/(?<=_)|(?=\.)|(?<=[a-z])(?=[A-Z])/);
  return (
    <>
      {parts
        .filter((p) => p !== '')
        .map((part, i) => (
          <Fragment key={i}>
            {i > 0 ? <wbr /> : null}
            {part}
          </Fragment>
        ))}
    </>
  );
}
